import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { Loader2, Calendar, Tag, ArrowLeft, BookOpen, Sparkles, Star, Diamond, Gem, Crown, Wrench, Server, Code, HardDrive, Shield, Award, Zap, Cloud } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { PublicLayout } from '../../components/layout/PublicLayout';
import CommentSection from '../../components/blog/CommentSection';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { useBlogStore } from '../../stores/blogStore';
import { useTenantContext } from '../../context/TenantContext';
import { ImageGallery } from '../../components/blog/ImageGallery';
import { applyDomainFilterToQuery } from '../../hooks/useDomainQuery';

// Mapping d'icônes pour personnalisation
const iconMap: Record<string, React.ElementType> = {
  BookOpen,
  Sparkles,
  Star,
  Diamond,
  Gem,
  Crown,
  Wrench,
  Server,
  Code,
  HardDrive,
  Shield,
  Award,
  Zap,
  Cloud,
};

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image_url: string;
  gallery_images?: string[];
  created_at: string;
  updated_at: string;
  categories: { id: string; name: string }[];
  tags: string[];
}

// Animation variants (conservés)
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

export const BlogPostDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { setBlogCategory } = useBlogStore();
  const { tenant, previewConfig } = useTenantContext();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Thème Blog (preview > tenant)
  const blogTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'BookOpen', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);

  // Configuration SEO personnalisée par tenant (doit être avant les returns conditionnels)
  const seoConfig = React.useMemo(() => {
    if (!post) return null;
    
    const seo = tenant?.config?.seo || {};
    const currentDomain = window.location.hostname;
    
    return {
      title: `${post.title} | ${tenant?.name || 'Blog'}`,
      description: post.excerpt || `Découvrez l'article : ${post.title}`,
      image: post.featured_image_url || seo.ogImage || tenant?.logo || tenant?.icon || "https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/media/tenant/3/1754983837000-2gd5is5zflc.JPG",
      url: `https://${currentDomain}/blog/${post.slug}`
    };
  }, [post, tenant]);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;

      setLoading(true);
      setError(null);
      try {
        // Construire la requête avec filtre de domaine
        let query = supabase
          .from('blog_posts')
          .select(`
            *,
            categories:blog_post_categories(
              category:blog_categories(id, name)
            )
          `)
          .eq('slug', slug)
          .eq('status', 'published');
          
        // Appliquer le filtre domaine avec fallback sur tenant_id
        query = applyDomainFilterToQuery(query);
        
        const { data, error } = await query.single();

        if (error || !data) {
          throw new Error('Article non trouvé');
        }

        const formattedPost: BlogPost = {
          ...data,
          categories: data.categories
            ? data.categories.map((item: any) => item.category)
            : [],
          tags: data.tags || [],
          gallery_images: data.gallery_images || [],
        };
        setPost(formattedPost);

      } catch (err) {
        console.error('Erreur lors de la récupération de l\'article:', err);
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-screen relative overflow-hidden">
          {/* Fond avec la nouvelle palette de couleurs SIS */}
          <div 
            className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
            style={{
              backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
              backgroundAttachment: 'fixed'
            }}
          >
            {blogTheme.overlay.enabled && (
              <div className="absolute inset-0 backdrop-blur-[2px]" style={{
                backgroundImage: blogTheme.overlay.useGradient
                  ? `linear-gradient(${(
                      {
                        'to-b': 'to bottom',
                        'to-t': 'to top',
                        'to-r': 'to right',
                        'to-l': 'to left',
                        'to-tr': 'to top right',
                        'to-br': 'to bottom right',
                        'to-tl': 'to top left',
                        'to-bl': 'to bottom left',
                      } as Record<string, string>
                    )[blogTheme.overlay.orientation] || 'to bottom'},
                      ${hexToRgba(blogTheme.overlay.color1, blogTheme.overlay.opacity1)},
                      ${hexToRgba(blogTheme.overlay.color2, blogTheme.overlay.opacity2)},
                      ${hexToRgba(blogTheme.overlay.color3, blogTheme.overlay.opacity3)}
                    )`
                  : undefined,
                backgroundColor: !blogTheme.overlay.useGradient
                  ? hexToRgba(blogTheme.overlay.color1, blogTheme.overlay.opacity2)
                  : undefined,
              }} />
            )}
          </div>
          
          <div className="flex flex-col justify-center items-center h-screen relative z-10">
            <div className="relative">
              <div className="w-16 h-16 border-4 rounded-full animate-spin"
                style={{
                  borderColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.2),
                  borderTopColor: tenant?.config?.color1 || '#fe8d17'
                }}
              ></div>
              <div className="absolute inset-0 w-16 h-16 border-4 border-transparent rounded-full animate-spin" 
                style={{ 
                  borderTopColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.3),
                  animationDirection: 'reverse',
                  animationDuration: '1.5s'
                }}
              ></div>
            </div>
            <span className="mt-6 text-xl font-medium" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Chargement de l'article...</span>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (error || !post) {
    return (
      <PublicLayout>
        <div className="min-h-screen relative overflow-hidden">
          {/* Fond avec la nouvelle palette de couleurs SIS */}
          <div 
            className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
            style={{
              backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
              backgroundAttachment: 'fixed'
            }}
          >
            {blogTheme.overlay.enabled && (
              <div className="absolute inset-0 backdrop-blur-[2px]" style={{
                backgroundImage: blogTheme.overlay.useGradient
                  ? `linear-gradient(${(
                      {
                        'to-b': 'to bottom',
                        'to-t': 'to top',
                        'to-r': 'to right',
                        'to-l': 'to left',
                        'to-tr': 'to top right',
                        'to-br': 'to bottom right',
                        'to-tl': 'to top left',
                        'to-bl': 'to bottom left',
                      } as Record<string, string>
                    )[blogTheme.overlay.orientation] || 'to bottom'},
                      ${hexToRgba(blogTheme.overlay.color1, blogTheme.overlay.opacity1)},
                      ${hexToRgba(blogTheme.overlay.color2, blogTheme.overlay.opacity2)},
                      ${hexToRgba(blogTheme.overlay.color3, blogTheme.overlay.opacity3)}
                    )`
                  : undefined,
                backgroundColor: !blogTheme.overlay.useGradient
                  ? hexToRgba(blogTheme.overlay.color1, blogTheme.overlay.opacity2)
                  : undefined,
              }} />
            )}
          </div>
          
          <div className="container mx-auto py-12 relative z-10">
            <div className="text-center py-20">
              <div className="mb-8 relative inline-block">
                <BookOpen className="w-16 h-16 mx-auto mb-4" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
              </div>
              <h2 className="text-3xl font-bold mb-4" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                Article introuvable
              </h2>
              <p className="mb-6 max-w-md mx-auto" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>
                L'article que vous recherchez n'existe pas ou a été déplacé.
              </p>
              <Button asChild className="shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                style={{
                  backgroundColor: tenant?.config?.color1 || '#fe8d17',
                  color: tenant?.config?.color3 || '#ffffff',
                  backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                  borderColor: `${tenant?.config?.color3 || '#fe8d17'}4d`,
                  boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                }}
              >
                <Link to="/blog" className="flex items-center">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Retour au blog
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      {seoConfig && (
        <SEOMetaTags
          title={seoConfig.title}
          description={seoConfig.description}
          image={seoConfig.image}
          url={seoConfig.url}
        />
      )}

      <div className="min-h-screen relative overflow-hidden">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
            backgroundAttachment: 'fixed'
          }}
        >
          {blogTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: blogTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[blogTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(blogTheme.overlay.color1, blogTheme.overlay.opacity1)},
                    ${hexToRgba(blogTheme.overlay.color2, blogTheme.overlay.opacity2)},
                    ${hexToRgba(blogTheme.overlay.color3, blogTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !blogTheme.overlay.useGradient
                ? hexToRgba(blogTheme.overlay.color1, blogTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs personnalisables */}
        <div className="absolute inset-0 overflow-hidden z-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.08) }} />
          <div className="absolute top-3/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.08), animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-3/4 w-48 h-48 rounded-full blur-2xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.15), animationDelay: '4s' }} />
        </div>
        
        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {blogTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(blogTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[blogTheme.icons[0].icon] || BookOpen, { size: blogTheme.icons[0].size })}
            </motion.div>
          )}
          {/* Top-Right */}
          {blogTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(blogTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[blogTheme.icons[1].icon] || Sparkles, { size: blogTheme.icons[1].size })}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {blogTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(blogTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[blogTheme.icons[2].icon] || Diamond, { size: blogTheme.icons[2].size })}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {blogTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(blogTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[blogTheme.icons[3].icon] || Star, { size: blogTheme.icons[3].size })}
            </motion.div>
          )}
        </div>

        {/* Contenu de la page */}
        <div className="relative z-10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 md:pt-24 md:pb-20">
            <motion.div 
              className="mb-8 max-w-4xl mx-auto"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Button asChild variant="outline" size="sm" className="transition-all duration-200"
                style={{
                  borderColor: `${tenant?.config?.color3 || '#fe8d17'}80`,
                  color: tenant?.config?.color3 || '#ffffff'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${tenant?.config?.color3 || '#fe8d17'}1a`;
                  e.currentTarget.style.borderColor = tenant?.config?.color3 || '#fe8d17';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = `${tenant?.config?.color3 || '#fe8d17'}80`;
                }}
              >
                <Link to="/blog" className="flex items-center">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Retour au Blog
                </Link>
              </Button>
            </motion.div>

            <motion.article 
              className="max-w-4xl mx-auto"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              {/* Header de l'article - Style ECATHEBA */}
              <header className="mb-12 relative">
                <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
                  className="text-center mb-8"
                >
                  {post.categories.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-2 mb-6">
                      {post.categories.map((category) => (
                        <Badge 
                          key={category.id} 
                          className="backdrop-blur-sm cursor-pointer transition-colors"
                          style={{
                            backgroundColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.2),
                            color: tenant?.config?.color3 || '#fe8d17',
                            borderColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.3)
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3);
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2);
                          }}
                          onClick={() => {
                            setBlogCategory(category.id);
                            navigate('/blog');
                          }}
                        >
                          {category.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 drop-shadow-lg" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                    {post.title}
                  </h1>
                </motion.div>
                
                <div className="flex items-center justify-center mb-8" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>
                  <Calendar className="mr-2 h-4 w-4" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                  <time dateTime={post.created_at}>
                    {format(new Date(post.created_at), 'dd MMMM yyyy', { locale: fr })}
                  </time>
                </div>
                
                {post.featured_image_url && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="relative w-full h-[30vh] md:h-[50vh] rounded-xl overflow-hidden shadow-2xl border"
                    style={{
                      borderColor: `${tenant?.config?.color3 || '#fe8d17'}4d`
                    }}
                  >
                    <img
                      src={post.featured_image_url}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t to-transparent z-10" style={{ backgroundImage: `linear-gradient(to top, ${hexToRgba(tenant?.config?.color2 || '#008aef', 0.5)}, transparent)` }}></div>
                  </motion.div>
                )}
              </header>

              {/* Contenu de l'article */}
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="bg-white/5 backdrop-blur-sm rounded-xl border text-white p-6 md:p-10 mb-8 shadow-2xl transition-all duration-500 hover:shadow-3xl"
                style={{
                  borderColor: `${tenant?.config?.color3 || '#fe8d17'}33`
                }}
              >
                {post.excerpt && (
                  <div className="text-xl font-medium mb-8 italic border-l-4 pl-4 py-2"
                    style={{
                      color: tenant?.config?.color3 || '#ffffff',
                      borderColor: tenant?.config?.color3 || '#fe8d17'
                    }}
                  >
                    {post.excerpt}
                  </div>
                )}

                <div 
                  className="prose max-w-none prose-lg prose-headings:font-display prose-headings:font-bold prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-4 prose-blockquote:pl-6 prose-blockquote:py-4 prose-blockquote:pr-4 prose-blockquote:rounded-r-md prose-blockquote:not-italic prose-img:rounded-xl prose-img:shadow-lg prose-img:mx-auto prose-img:my-8 prose-ul:list-disc prose-ol:list-decimal prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto"
                  style={{
                    '--tw-prose-headings': tenant?.config?.color3 || '#ffffff',
                    '--tw-prose-body': tenant?.config?.color3 || '#ffffff',
                    '--tw-prose-strong': tenant?.config?.color1 || '#fe8d17',
                    '--tw-prose-links': tenant?.config?.color1 || '#fe8d17',
                    '--tw-prose-quote-borders': tenant?.config?.color1 || '#fe8d17',
                    '--tw-prose-quotes': tenant?.config?.color3 || '#ffffff',
                    '--tw-prose-list-markers': tenant?.config?.color1 || '#fe8d17',
                    '--tw-prose-code': tenant?.config?.color3 || '#ffffff',
                    '--tw-prose-pre-code': tenant?.config?.color3 || '#ffffff',
                    '--tw-prose-pre-bg': hexToRgba(tenant?.config?.color2 || '#008aef', 0.1),
                    '--tw-prose-code-bg': hexToRgba(tenant?.config?.color2 || '#008aef', 0.1),
                    '--tw-prose-blockquote-bg': hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1)
                  } as React.CSSProperties}
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />

                {post.tags && post.tags.length > 0 && (
                  <div className="mt-8 pt-6 border-t" style={{ borderColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.2) }}>
                    <div className="flex items-center flex-wrap gap-2">
                      <Tag className="h-4 w-4" style={{ color: tenant?.config?.color3 || '#fe8d17' }} />
                      {post.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="backdrop-blur-sm"
                          style={{
                            backgroundColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.1),
                            borderColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.3),
                            color: tenant?.config?.color3 || '#ffffff'
                          }}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
              
              {/* Galerie d'images */}
              {post.gallery_images && post.gallery_images.length > 0 && (
                <ImageGallery 
                  images={post.gallery_images} 
                  className="mt-8" 
                />
              )}
            </motion.article>

            {/* Section Commentaires */}
            <motion.div 
              className="max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border text-white p-6 md:p-8 shadow-2xl transition-all duration-500 hover:shadow-3xl"
                style={{
                  borderColor: `${tenant?.config?.color3 || '#fe8d17'}33`
                }}
              >
                <CommentSection postId={post.id} />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default BlogPostDetail;